import { useTranslation } from 'react-i18next'
/**
 * Referral Stats By Level Component
 * Shows users invited and earnings grouped by level (L1, L2, L3)
 */

import { useQuery } from '@tanstack/react-query'
import { Users, DollarSign, Coins, TrendingUp } from 'lucide-react'
import { api } from '../../services/api'
import { useConfig } from '../../contexts/ConfigContext'

interface LevelStats {
  users: Array<{
    id: string
    username: string | null
    walletAddress: string | null
    level: number | null
    joinedAt: string
  }>
  totalEarnedUSDT: number
  totalEarnedTAKARA: number
  pendingUSDT: number
  pendingTAKARA: number
  userCount: number
}

interface ReferralStatsData {
  byLevel: {
    L1: LevelStats
    L2: LevelStats
    L3: LevelStats
  }
  totals: {
    totalUsers: number
    totalEarnedUSDT: number
    totalEarnedTAKARA: number
    totalPendingUSDT: number
    totalPendingTAKARA: number
  }
}

export default function ReferralStatsByLevel() {
  const { t } = useTranslation(['referral', 'common'])
  const config = useConfig()
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-referral-stats'],
    queryFn: async () => {
      const response = await api.getUserReferralStats()
      return response.data as ReferralStatsData | null
    },
    retry: false,
    staleTime: 60 * 1000, // 1 minute cache
  })

  if (isLoading) {
    return (
      <div className="bg-background-card rounded-xl border border-green-900/20 p-6">
        <div className="animate-pulse h-48 bg-background-elevated rounded"></div>
      </div>
    )
  }

  if (error || !data) {
    return null
  }

  const { byLevel, totals } = data

  // Don't show if no users invited
  if (totals.totalUsers === 0) {
    return null
  }

  // Get referral rates from config (CASCADE v3.0)
  const usdtRates = config.referral.usdtRates
  const levels = [
    { key: 'L1' as const, label: 'Level 1 (Direct)', percent: `${(usdtRates[1] || 0.10) * 100}%`, color: 'gold' },
    { key: 'L2' as const, label: 'Level 2', percent: `${(usdtRates[2] || 0.06) * 100}%`, color: 'teal' },
    { key: 'L3' as const, label: 'Level 3', percent: `${(usdtRates[3] || 0.03) * 100}%`, color: 'purple' },
  ]

  return (
    <div className="bg-background-card rounded-xl border border-green-900/20 p-6 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-gold-500/20 rounded-lg">
          <TrendingUp className="h-5 w-5 text-gold-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">Referral Earnings by Level</h3>
          <p className="text-xs text-gray-400">Users you invited and earnings from each level</p>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Users className="h-4 w-4 text-teal-400" />
            <span className="text-xs text-gray-400">Total Invited</span>
          </div>
          <p className="text-xl font-bold text-white">{totals.totalUsers}</p>
        </div>
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <DollarSign className="h-4 w-4 text-gold-400" />
            <span className="text-xs text-gray-400">Earned USDT</span>
          </div>
          <p className="text-xl font-bold text-gold-400">${totals.totalEarnedUSDT.toFixed(2)}</p>
        </div>
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Coins className="h-4 w-4 text-green-400" />
            <span className="text-xs text-gray-400">Earned TAKARA</span>
          </div>
          <p className="text-xl font-bold text-green-400">{totals.totalEarnedTAKARA.toFixed(2)}</p>
        </div>
        <div className="bg-background-elevated rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="h-4 w-4 text-purple-400" />
            <span className="text-xs text-gray-400">Pending</span>
          </div>
          <p className="text-sm font-bold text-white">
            ${totals.totalPendingUSDT.toFixed(2)} / {totals.totalPendingTAKARA.toFixed(2)}
          </p>
        </div>
      </div>

      {/* Level Breakdown */}
      <div className="space-y-4">
        {levels.map(({ key, label, percent, color }) => {
          const levelData = byLevel[key]
          if (levelData.userCount === 0) return null

          const colorClasses = {
            gold: 'border-gold-500/30 bg-gold-500/5',
            teal: 'border-teal-500/30 bg-teal-500/5',
            purple: 'border-purple-500/30 bg-purple-500/5',
          }

          const textColorClasses = {
            gold: 'text-gold-400',
            teal: 'text-teal-400',
            purple: 'text-purple-400',
          }

          return (
            <div
              key={key}
              className={`border rounded-lg p-4 ${colorClasses[color as keyof typeof colorClasses]}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-white">{label}</h4>
                  <p className="text-xs text-gray-400">You earn {percent} from their claims</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-400">Users</div>
                  <div className={`text-xl font-bold ${textColorClasses[color as keyof typeof textColorClasses]}`}>
                    {levelData.userCount}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                <div>
                  <div className="text-xs text-gray-400 mb-1">Earned USDT</div>
                  <div className="text-sm font-semibold text-gold-400">
                    ${levelData.totalEarnedUSDT.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Earned TAKARA</div>
                  <div className="text-sm font-semibold text-green-400">
                    {levelData.totalEarnedTAKARA.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Pending USDT</div>
                  <div className="text-sm font-semibold text-yellow-400">
                    ${levelData.pendingUSDT.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Pending TAKARA</div>
                  <div className="text-sm font-semibold text-yellow-400">
                    {levelData.pendingTAKARA.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* User List */}
              {levelData.users.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-700">
                  <div className="text-xs text-gray-400 mb-2">Invited Users:</div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {levelData.users.map((user) => (
                      <div
                        key={user.id}
                        className="text-xs text-gray-300 flex items-center justify-between py-1"
                      >
                        <span>
                          {user.username || user.walletAddress?.slice(0, 8) + '...' || 'Anonymous'}
                        </span>
                        <span className="text-gray-500">
                          {new Date(user.joinedAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
